import React from 'react';
import { Link, useLocation } from 'wouter';
import { 
  Lightbulb, 
  AlertTriangle, 
  Info, 
  ArrowLeft, 
  FileText,
  Book,
  CheckCircle,
  PlusCircle,
  MinusCircle
} from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import Slideshow, { SlideContent } from '@/components/lectures/Slideshow';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';

// KeyTakeaway component
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Lightbulb className="text-blue-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-blue-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Clinical Note component
const ClinicalNote = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <AlertTriangle className="text-amber-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-amber-800 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Evidence Box component
const EvidenceBox = ({ title, children }: { title: string, children: React.ReactNode }) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-5 my-6">
      <div className="flex items-center mb-2">
        <Info className="text-gray-600 h-5 w-5 mr-2" />
        <h4 className="font-semibold text-gray-900">{title}</h4>
      </div>
      <div className="text-gray-700 text-sm">
        {children}
      </div>
    </div>
  );
};

const lectureSlides: SlideContent[] = [
  {
    title: "The Big Picture Shift: From Region to Resolution",
    content: (
      <>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Semaglutide's Action: A Major Update</h2>
          <p className="text-lg mb-4">
            Our understanding of how GLP-1 agonists drive weight loss has fundamentally shifted from a general region to a specific cell type.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-red-50 p-6 rounded-lg border border-red-200">
            <h3 className="font-semibold text-red-800 mb-3 flex items-center">
              <MinusCircle className="h-5 w-5 mr-2" />
              Previous Understanding
            </h3>
            <p className="text-red-700 text-sm mb-3">
              Semaglutide acts on the hindbrain (Dorsal Vagal Complex - DVC) to suppress appetite. 
              This was effective but mechanistically vague, lumping therapeutic effects and side effects together.
            </p>
            <div className="bg-red-100 p-3 rounded">
              <p className="text-xs text-red-600 italic">
                General brain response model - limited precision for targeting
              </p>
            </div>
          </div>

          <div className="bg-green-50 p-6 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-3 flex items-center">
              <PlusCircle className="h-5 w-5 mr-2" />
              New Evidence (Teixidor-Deulofeu et al.)
            </h3>
            <p className="text-green-700 text-sm mb-3">
              This paper pinpoints the primary mediators: <strong>Adcyap1+ neurons</strong> within the DVC. 
              These specific neurons are essential for the drug's benefits.
            </p>
            <div className="bg-green-100 p-3 rounded">
              <p className="text-xs text-green-600 italic">
                Distinct, identifiable neuronal population with minimal role in nausea
              </p>
            </div>
          </div>
        </div>

        <KeyTakeaway>
          Semaglutide's profound effects on appetite and fat loss are not a generalized brain response. 
          They are driven by a distinct, identifiable neuronal population, which has a surprisingly minimal role in the drug's main side effect—nausea.
        </KeyTakeaway>

        <div className="bg-gray-50 p-6 rounded-lg mb-6">
          <h3 className="font-semibold text-gray-800 mb-4 text-center">Mechanistic Evolution: Old View vs. New Precision View</h3>
          <div className="flex justify-center">
            <svg width="600" height="300" viewBox="0 0 600 300" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <style>
                  {`
                    .label { font-family: Arial, sans-serif; font-size: 14px; fill: #333; }
                    .title-label { font-family: Arial, sans-serif; font-size: 16px; font-weight: bold; fill: #000; }
                    .arrow-head { fill: #555; }
                    .brain-outline { fill: #e0e0e0; stroke: #b0b0b0; stroke-width: 1.5; }
                    .old-target { fill: #d9534f; opacity: 0.5; }
                    .new-target { fill: #5cb85c; }
                  `}
                </style>
              </defs>
              
              {/* Left Side: Old View */}
              <text x="150" y="30" className="title-label" textAnchor="middle">Old View</text>
              <path d="M100,60 C50,120 100,200 180,185 C220,178 205,100 180,60 C160,35 120,35 100,60 Z" className="brain-outline"/>
              <circle cx="140" cy="150" r="35" className="old-target"/>
              <text x="140" y="158" className="label" textAnchor="middle" fill="#fff" fontWeight="bold">Hindbrain</text>
              <text x="140" y="172" className="label" textAnchor="middle" fill="#fff" fontSize="11">(General DVC)</text>
              
              <path d="M60,110 L105,135" stroke="#555" strokeWidth="2"/>
              <polygon points="100,130 105,135 95,135" className="arrow-head"/>
              <text x="40" y="105" className="label">Semaglutide</text>
              
              {/* Right Side: New View */}
              <text x="450" y="30" className="title-label" textAnchor="middle">New, Precise View</text>
              <path d="M400,60 C350,120 400,200 480,185 C520,178 505,100 480,60 C460,35 420,35 400,60 Z" className="brain-outline"/>
              <circle cx="440" cy="150" r="12" className="new-target"/>
              <line x1="452" y1="150" x2="480" y2="130" stroke="#333" strokeWidth="1"/>
              <text x="485" y="125" className="label">Adcyap1+</text>
              <text x="485" y="140" className="label">Neurons</text>
              
              <path d="M360,110 L425,140" stroke="#555" strokeWidth="2"/>
              <polygon points="420,135 425,140 415,140" className="arrow-head"/>
              <text x="340" y="105" className="label">Semaglutide</text>
              
              {/* Comparison arrows */}
              <text x="300" y="250" className="label" textAnchor="middle" fontSize="12">
                Precision targeting enables separation of therapeutic effects from side effects
              </text>
            </svg>
          </div>
        </div>

        <ClinicalNote>
          This shift from regional to cellular precision represents a paradigm change in our understanding of GLP-1 receptor pharmacology, 
          enabling more targeted therapeutic approaches and better patient counseling strategies.
        </ClinicalNote>
      </>
    ),
  },
  {
    title: "The Satiety Circuit: How It Works & Why It Matters",
    content: (
      <>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">A Decoupled Pathway for Satiety and Fat Loss</h2>
          <p className="text-lg mb-4">
            This study reveals a precise circuit that separates the desired effects from the adverse ones.
          </p>
        </div>

        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <h3 className="font-semibold text-blue-800 mb-4">The Three-Step Pathway</h3>
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 font-bold">1</div>
              <div>
                <h4 className="font-medium text-blue-800">Entry Point</h4>
                <p className="text-blue-700 text-sm">
                  Semaglutide accesses the brain via the <strong>Area Postrema (AP)</strong>, a region lacking a blood-brain barrier, 
                  where it binds to GLP-1 receptors.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 font-bold">2</div>
              <div>
                <h4 className="font-medium text-blue-800">The Relay</h4>
                <p className="text-blue-700 text-sm">
                  AP neurons then activate the large population of <strong>NTS Adcyap1+ neurons</strong>.
                </p>
                <div className="mt-2 p-3 bg-blue-100 rounded">
                  <p className="text-xs text-blue-600">
                    <strong>What are Adcyap1+ neurons?</strong> These neurons produce PACAP, a powerful neuropeptide that 
                    regulates metabolism and feeding. Its signaling is known to be potent and long-lasting, explaining the sustained effects of semaglutide.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 font-bold">3</div>
              <div>
                <h4 className="font-medium text-blue-800">The Result</h4>
                <p className="text-blue-700 text-sm mb-2">Ablating (removing) these Adcyap1+ neurons:</p>
                <ul className="text-blue-700 text-sm space-y-1 ml-4">
                  <li>• <strong>Dramatically reduced</strong> semaglutide-induced appetite suppression and weight loss</li>
                  <li>• <strong>Preferentially reversed FAT loss</strong>, with minimal impact on lean mass loss</li>
                  <li>• Had only a <strong>modest effect on nausea</strong> (Conditioned Taste Aversion)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <KeyTakeaway>
          The primary therapeutic pathway (satiety, fat loss) is largely distinct from the primary nausea pathway. 
          This separation opens possibilities for more targeted therapies with fewer side effects.
        </KeyTakeaway>

        <div className="bg-gray-50 p-6 rounded-lg mb-6">
          <h3 className="font-semibold text-gray-800 mb-4 text-center">Semaglutide Neural Circuit Pathway</h3>
          <div className="flex justify-center">
            <svg width="650" height="350" viewBox="0 0 650 350" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <style>
                  {`
                    .pathway-box { rx: 5; ry: 5; stroke-width: 1.5; }
                    .pathway-label { font-family: Arial, sans-serif; font-size: 14px; text-anchor: middle; }
                    .pathway-title { font-family: Arial, sans-serif; font-size: 16px; font-weight: bold; text-anchor: middle; }
                    .pathway-arrow { stroke: #333; stroke-width: 2; marker-end: url(#arrowhead); }
                    .dashed-arrow { stroke: #aaa; stroke-width: 2; stroke-dasharray: 5,5; marker-end: url(#arrowhead-dashed); }
                  `}
                </style>
                <marker id="arrowhead" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#333" />
                </marker>
                <marker id="arrowhead-dashed" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#aaa" />
                </marker>
              </defs>
              
              {/* Input */}
              <rect x="20" y="150" width="120" height="60" className="pathway-box" fill="#f0f8ff" stroke="#add8e6"/>
              <text x="80" y="175" className="pathway-label">Semaglutide</text>
              <text x="80" y="195" className="pathway-label">(in blood)</text>
              
              {/* Area Postrema */}
              <rect x="200" y="50" width="180" height="60" className="pathway-box" fill="#fff0f5" stroke="#ffb6c1"/>
              <text x="290" y="75" className="pathway-title">Area Postrema (AP)</text>
              <text x="290" y="95" className="pathway-label">GLP-1R / No BBB</text>
              
              {/* NTS Adcyap1+ */}
              <rect x="200" y="150" width="180" height="60" className="pathway-box" fill="#e6ffe6" stroke="#90ee90"/>
              <text x="290" y="175" className="pathway-title">NTS Adcyap1+</text>
              <text x="290" y="195" className="pathway-label">(The Key Player)</text>
              
              {/* Aversion Centers */}
              <rect x="450" y="20" width="140" height="80" className="pathway-box" fill="#d3d3d3" stroke="#a9a9a9"/>
              <text x="520" y="50" className="pathway-label">Aversion Centers</text>
              <text x="520" y="70" className="pathway-label">(e.g., PBel, CeA)</text>
              <text x="520" y="90" className="pathway-label" fontWeight="bold" fill="#c00">Nausea</text>
              
              {/* Satiety Centers */}
              <rect x="450" y="240" width="140" height="80" className="pathway-box" fill="#d4edda" stroke="#5cb85c"/>
              <text x="520" y="270" className="pathway-label">Satiety Centers</text>
              <text x="520" y="290" className="pathway-label">(e.g., PVH, DMH)</text>
              <text x="520" y="310" className="pathway-label" fontWeight="bold" fill="#006400">Fat Loss</text>
              
              {/* Arrows */}
              <path d="M 145 180 Q 165 120, 195 80" className="pathway-arrow" fill="none"/>
              <path d="M 145 180 L 195 180" className="pathway-arrow" fill="none"/>
              <path d="M 290 115 L 290 145" className="pathway-arrow" fill="none"/>
              <path d="M 385 180 L 445 280" className="pathway-arrow" fill="none"/>
              <path d="M 385 80 L 445 80" className="dashed-arrow" fill="none"/>
              <path d="M 385 180 L 445 80" className="dashed-arrow" fill="none"/>
              
              {/* Legend */}
              <text x="325" y="340" className="pathway-label" fontSize="12">
                Solid arrows: Primary therapeutic pathway | Dashed arrows: Secondary nausea pathway
              </text>
            </svg>
          </div>
        </div>

        <ClinicalNote>
          Understanding this pathway separation allows clinicians to optimize dosing strategies, focusing on maximizing 
          satiety benefits while minimizing nausea through careful titration and patient education.
        </ClinicalNote>
      </>
    ),
  },
  {
    title: "Novel Insights & Actionable Practice Changes",
    content: (
      <>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">From Bench to Bedside: What to Change Now</h2>
          <p className="text-lg mb-4">
            This new understanding provides immediate, actionable insights for your practice.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-3">Novel Insight #1</h3>
            <h4 className="font-medium text-purple-700 mb-2">Centrally-Mediated Metabolic Reprogramming</h4>
            <p className="text-purple-700 text-sm">
              Activating Adcyap1+ neurons promotes fat utilization (ketogenesis, lower RER) <em>independent of food intake</em>. 
              Semaglutide isn't just a calorie-reduction tool; it actively shifts the body's entire metabolic state towards fat burning 
              via this central mechanism.
            </p>
            <div className="mt-3 p-3 bg-purple-100 rounded">
              <p className="text-xs text-purple-600 italic">
                Core principle of metabolic health and longevity
              </p>
            </div>
          </div>

          <div className="bg-indigo-50 p-6 rounded-lg border border-indigo-200">
            <h3 className="font-semibold text-indigo-800 mb-3">Novel Insight #2</h3>
            <h4 className="font-medium text-indigo-700 mb-2">Sustained Signaling via PACAP/cAMP</h4>
            <p className="text-indigo-700 text-sm">
              The long-lasting effects on appetite are likely due to the Adcyap1 gene product, PACAP, activating the cAMP/PKA signaling cascade. 
              This pathway can induce long-term changes in gene expression, creating a durable state of satiety that outlasts the drug's immediate presence.
            </p>
            <div className="mt-3 p-3 bg-indigo-100 rounded">
              <p className="text-xs text-indigo-600 italic">
                Explains sustained therapeutic effects
              </p>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg mb-6">
          <h3 className="font-semibold text-amber-800 mb-4">Practice Changes & Counseling Points</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-amber-100">
                  <th className="border border-amber-300 p-3 text-left font-bold text-amber-800">Area</th>
                  <th className="border border-amber-300 p-3 text-left font-bold text-amber-800">Actionable Takeaway</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-amber-300 p-3 bg-amber-50 font-medium text-amber-700">Patient Counseling</td>
                  <td className="border border-amber-300 p-3 text-amber-700 text-sm">
                    Explain that fullness and nausea are likely separate effects. This validates their experience and 
                    reinforces dose titration to find their optimal therapeutic window.
                  </td>
                </tr>
                <tr>
                  <td className="border border-amber-300 p-3 bg-amber-50 font-medium text-amber-700">Body Composition</td>
                  <td className="border border-amber-300 p-3 text-amber-700 text-sm">
                    Emphasize that semaglutide, via this pathway, is engineered to <strong>preferentially target fat loss</strong>. 
                    This is a powerful motivator and addresses patient fears about sarcopenia.
                  </td>
                </tr>
                <tr>
                  <td className="border border-amber-300 p-3 bg-amber-50 font-medium text-amber-700">Future Outlook</td>
                  <td className="border border-amber-300 p-3 text-amber-700 text-sm">
                    This discovery paves the way for next-generation obesity drugs that could selectively activate Adcyap1+ neurons, 
                    potentially offering robust weight loss with even fewer side effects.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <KeyTakeaway>
          This research transforms semaglutide from a "weight loss drug with side effects" into a "precision metabolic reprogramming tool" 
          that targets specific neural circuits. This reframing enhances patient confidence and clinical outcomes.
        </KeyTakeaway>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <h4 className="font-medium text-green-800 mb-1">Enhanced Counseling</h4>
            <p className="text-xs text-green-700">Separate therapeutic and adverse pathways</p>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <CheckCircle className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <h4 className="font-medium text-blue-800 mb-1">Targeted Fat Loss</h4>
            <p className="text-xs text-blue-700">Preferential fat metabolism activation</p>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 text-center">
            <CheckCircle className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <h4 className="font-medium text-purple-800 mb-1">Future Therapies</h4>
            <p className="text-xs text-purple-700">Next-generation precision drugs</p>
          </div>
        </div>

        <EvidenceBox title="Research References">
          <div className="text-sm space-y-2">
            <p>
              <strong>1.</strong> Teixidor-Deulofeu, J., et al. (2025). Semaglutide effects on energy balance are mediated by Adcyap1+ neurons 
              in the dorsal vagal complex. <em>Cell Metabolism, 37</em>, 1-17.
            </p>
            <p>
              <strong>2.</strong> Existing Course Lectures: Advanced Therapeutic Innovations. Section on GLP-1 Agonists.
            </p>
            <p className="text-gray-600 italic mt-3">
              This research represents a fundamental shift in our mechanistic understanding of GLP-1 receptor pharmacology, 
              moving from regional to cellular precision in therapeutic targeting.
            </p>
          </div>
        </EvidenceBox>
      </>
    ),
  }
];

const SemaglutideMechanismPrecisionUpdate = () => {
  const [, navigate] = useLocation();
  
  return (
    <MentorshipAccessGate>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow">
          <div className="flex items-center justify-between mb-6">
            <Button 
              variant="ghost" 
              className="flex items-center text-blue-600"
              onClick={() => navigate("/mentorship")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Mentorship
            </Button>
          </div>
          
          <div>
            {/* Breadcrumb Navigation */}
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href="/mentorship">Mentorship</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <span className="text-gray-600">Semaglutide's Mechanism: A Precision Update</span>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
              Semaglutide's Mechanism: A Precision Update for Longevity Practice
            </h1>
            <p className="text-[#7F8C8D] mb-6">
              From regional understanding to cellular precision: How new research transforms our approach to GLP-1 therapy with actionable clinical insights.
            </p>
            <div className="flex items-center gap-3 mb-8">
              <div className="flex items-center text-sm text-[#7F8C8D]">
                <FileText className="h-4 w-4 mr-1" />
                <span>Lecture Duration: Under 2 minutes</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-gray-300"></div>
              <div className="flex items-center text-sm text-[#7F8C8D]">
                <Book className="h-4 w-4 mr-1" />
                <span>Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-gray-300"></div>
              <div className="flex items-center text-sm text-[#7F8C8D]">
                <CheckCircle className="h-4 w-4 mr-1" />
                <span>Audience: Physicians new to longevity medicine</span>
              </div>
            </div>
            
            <Separator />
            
            <Slideshow slides={lectureSlides} />
            
            {/* Audio Content */}
            <div className="mt-8 bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Accompanying Audio</h3>
              <div className="flex justify-center">
                <iframe 
                  src="https://share.descript.com/embed/ObBA8qa6UQ2" 
                  width="640" 
                  height="360" 
                  frameBorder="0" 
                  allowFullScreen
                  className="rounded-lg shadow-sm"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    </MentorshipAccessGate>
  );
};

export default SemaglutideMechanismPrecisionUpdate;